import React, { useEffect, useState } from "react";
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LinearProgress from '@mui/material/LinearProgress';
import CustomSnackbar from '../common/CustomSnackbar';
import KCLogo from '../../assets/KCLogo.svg';
import ProfileMenu from "./ProfileMenu";
import FailureDialog from "../trackNtrace/FailureDialog";
import { carrierAndAnlystRoles, getEnumValue, tmtRoles } from "../../helpers";
import { errorFieldDrop, errorFieldPickup } from "../../constData/editErrorFieldsMapping";
import './Header.sass';

export default function Header({ authState, oktaAuth, user, userID }) {

  const { t } = useTranslation();
  const location = useLocation();
  const { userInfo: { role, tmtRole } } = useSelector(state => state?.user);
  const [tabIndex, setTabIndex] = React.useState(0);
  const { isLoading } = useSelector(state => state?.loadDetails);
  const { isLoading: isDrmTableDataLoading } = useSelector((state) => state?.drmTableData || {});
  const {isLoading: isLoadingMasterData, globalLoading } = useSelector((state) => state.masterData);
  const { isLoading: isLoadingUpdateDrmTable } = useSelector((state) => state?.drmUpdates);
  const { isLoading: coupaFileUploadLoading } = useSelector((state) => state?.drmCoupaFile);
  const { isLoading: dailyCancelRecord } = useSelector((state)=> state?.drmDailyRecordCancel);
  const { isLoading: flipFlagLoading } = useSelector((state)=> state?.drmFlipFlag || {});
  const {isLoading: isLoadingExcelData } = useSelector((state) => state.excelTableData);
  const { isLoading: isChangeLogDrmTableDataLoading } = useSelector((state)=> state.drmChangeLog);
  const { saved, errorMessage, savingLoading } = useSelector(({ edit: { saved, errorMessage, savingLoading } }) => ({ saved, errorMessage, savingLoading }));
  const { retriggered, errMsg, retriggerLoading } = useSelector(({ retrigger: { retriggered, errMsg, retriggerLoading } }) => ({ retriggered, errMsg, retriggerLoading }));
  const { commentsLoading, commentsErrMsg } = useSelector(state => state?.userComments);
  const { addCommentLoading } = useSelector(state => state.addNewComment);
  const { isLoadingCSV } = useSelector(state => state?.csvDetails);
  const [loading, setLoading] = useState(false);
  const { isCarrierServiceMetadataLoading } = useSelector(state => state.carrierMetadata);
  const { isLocMetadataLoading } = useSelector(state => state.locationMetadata);
  const [snack, setSnack] = useState({
    open: false,
    message: null,
    severity: 'success'
  });
  const [openDialog, setOpenDialog] = useState({
    open: false,
    message: [],
    loadId: null,
    failed: false
  });

  useEffect(() => {
    setLoading(isLoading || savingLoading || retriggerLoading || commentsLoading || addCommentLoading || isLoadingMasterData || isDrmTableDataLoading || isLoadingExcelData || isChangeLogDrmTableDataLoading || globalLoading || isLoadingUpdateDrmTable || coupaFileUploadLoading ||         dailyCancelRecord || flipFlagLoading);
      }, [isLoading, savingLoading, retriggerLoading, commentsLoading, addCommentLoading, isLoadingMasterData, isDrmTableDataLoading, isLoadingExcelData, isChangeLogDrmTableDataLoading, globalLoading, isLoadingUpdateDrmTable, coupaFileUploadLoading, dailyCancelRecord, flipFlagLoading]);

  useEffect(() => {
    if (saved?.completelySucessful || errorMessage) {
      setSnack({
        message: saved?.loadId ? `${t('load_id')} ${saved?.loadId} ${t('success')}` : errorMessage,
        severity: saved?.loadId ? 'success' : 'error',
        open: true
      });
    } else {
      let y = [];
      if (saved?.completelySucessful === false) {
        let x = saved?.failedFields?.forEach(item => {
          let field = item.stopType === 'pickup' ? getEnumValue(item.failedField, errorFieldPickup) : getEnumValue(item.failedField, errorFieldDrop)
          y.push(`${t(item.stopType)} ${item.stopType === 'pickup' ? item.stopNum : item.stopNum - 1} ${t('failed')} ${t(field)} ${t('due_to')} ${item.failureReason}`)
        }, [])
        setOpenDialog({
          open: true,
          message: y,
          loadId: saved?.loadId,
          failed: saved?.completelyFailed ? true : false
        })
      }
    }
  }, [saved?.completelySucessful, errorMessage]);

  useEffect(() => {
    if (retriggered?.completelySucessful || errMsg) { // snack for 200 complete suceess and failure (400, 404, 500 etc)
      setSnack({
        message: retriggered?.loadId ? `${t('load_id')} ${retriggered?.loadId} ${t(retriggered?.message)}` : errMsg,
        severity: retriggered?.loadId ? 'success' : 'error',
        open: true
      });
    } else if(retriggered?.status === 'good') { // snack for 204
      setSnack({
        message: `${t('load_id')} ${retriggered?.loadId} ${t(retriggered?.message)}`,
        severity: 'success',
        open: true
      });
    } else { // Dialog 409
      let y = [];
      if (retriggered?.completelySucessful === false) {
        let x = retriggered?.failedFields?.forEach(item => {
          let field = item.stopType === 'pickup' ? getEnumValue(item.failedField, errorFieldPickup) : getEnumValue(item.failedField, errorFieldDrop)
          y.push(`${t(item.stopType)} ${item.stopType === 'pickup' ? item.stopNum : item.stopNum - 1} ${t('failed')} ${t(field)} ${t('due_to')} ${item.failureReason}`)
        }, [])
        setOpenDialog({
          open: true,
          message: y,
          loadId: retriggered?.loadId,
          failed: retriggered?.completelyFailed ? true : false
        })
      }
    }
  }, [retriggered?.loadId, retriggered?.status, errMsg]);

  useEffect(() => {
    if(commentsErrMsg && commentsErrMsg !== 'success') {
      setSnack({
        message: t('search_comments_failure'),
        severity: 'error',
        open: true
      })
    }
  }, [commentsErrMsg]);

  useEffect(() => {
    setLoading(isLoading || savingLoading || isLoadingCSV || isCarrierServiceMetadataLoading || isLocMetadataLoading);
  }, [isLoading, savingLoading, isLoadingCSV, isCarrierServiceMetadataLoading, isLocMetadataLoading]);

  useEffect(() => {

    switch (location?.pathname.split('/')[2]) {
      case "tracking":
        setTabIndex(0);
        return;
      case "performance":
        setTabIndex(1);
        return;
      case "drm_tool":
        if (role && tmtRole) {
          setTabIndex(2);
          return;
        } else {
          setTabIndex(0);
          return;
        }
    }
    // let x = oktaAuth?.setOriginalUri(`${location?.pathname}${location?.search}`);
  }, [location?.pathname, role, tmtRole])

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getApptitle = (role, tmtRole) => {
    if(role && tmtRole) {
      return `${t("carrier_web_portal")} / ${t("tmt_title")}`;
    } else if(role) {
      return t("carrier_web_portal");
    } else {
      return t("tmt_title");
    }
  }

  return (
    <>
      <AppBar data-testid="header">
        <Toolbar className="toolbar">
          <img src={KCLogo} alt="Logo" className="logo" width={65} />
          <Typography variant="h1" className="appTitle">{getApptitle(role, tmtRole)}</Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            <Tabs id="tabs" value={tabIndex} onChange={handleTabChange} aria-label="tabs">
              {carrierAndAnlystRoles.includes(role) && <Tab
                label={<Typography variant={tabIndex === 0 ? "h2" : "h3"} className="tab">{t("track_n_trace")}</Typography>}
                id={0}
                data-testid="tabzero"
                to="/dashboard/tracking"
                component={Link}
              />}
              {carrierAndAnlystRoles.includes(role) && <Tab
                label={<Typography variant={tabIndex === 1 ? "h2" : "h3"} className="tab">{t("carrier_performance")}</Typography>}
                id={1}
                data-testid="tabone"
                to={'/dashboard/performance'}
                component={Link}
              />}
              {/* {tmtRoles.includes(tmtRole) && <Tab
                label={<Typography variant={tabIndex === 2 || (tabIndex === 0 && !role && tmtRole) ? "h2" : "h3"} className="tab">{t("tmt")}</Typography>}
                id={2}
                data-testid="tabtwo"
                to={'/dashboard/tmt'}
                component={Link}
              />} */}
              {tmtRoles.includes(tmtRole) &&<Tab
                label={
                  <Typography
                    variant={tabIndex === 2 ? "h2" : "h3"}
                    className="tab"
                  >
                    {t('drm_tool')}
                  </Typography>
                }
                id={2}
                data-testid="tabtwo"
                to={"/dashboard/drm_tool/daily_maintenance"}
                component={Link}
              />}
            </Tabs>
          </Box>
          {authState?.idToken &&
            <div className="appbarItems">
              <ProfileMenu oktaAuth={oktaAuth} user={user} userID={userID} />
            </div>
          }
        </Toolbar>
        {loading && <LinearProgress className="progress" />}
        <CustomSnackbar
          // autoHideDuration={3000}
          {...snack}
          handleClose={() => setSnack({
            ...snack,
            open: false
          })}
        />
      </AppBar>
      <FailureDialog
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
      />
    </>
  );

}